import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AuthProvider } from '@baaligames/shared-ui/lib/auth';
import { ThemeProvider } from '@baaligames/shared-ui/lib/theme';
import { ToastProvider } from '@baaligames/shared-ui/lib/toast';
import { I18nProvider } from '@baaligames/shared-ui/lib/i18n';
import './index.css';

// Hold React's first render until the Material Symbols font has
// actually loaded. The inline #boot-splash inside index.html stays
// visible during this window. Without the wait, icons would render
// as invisible placeholders (font-display: block) and then pop in
// half a second later; with the wait, the UI appears fully-formed
// from the first frame. 3s hard ceiling so a slow network or font
// failure never blocks the app forever.
const mount = () => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ThemeProvider>
        <I18nProvider>
          <ToastProvider>
            <BrowserRouter>
              <AuthProvider>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </ToastProvider>
        </I18nProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
};

if (typeof document !== 'undefined' && 'fonts' in document) {
  // `document.fonts.load(...)` forces the browser to actually fetch
  // the font if it hasn't started yet (more reliable than just
  // `document.fonts.ready`, which only resolves when currently-known
  // font loads settle — and a font not yet referenced never triggers
  // a fetch). A 3s timeout guards against broken networks / CDN.
  Promise.race([
    (document as any).fonts.load('24px "Material Symbols Rounded"'),
    new Promise((resolve) => setTimeout(resolve, 3000)),
  ]).then(mount).catch(mount);
} else {
  mount();
}

// Service worker DISABLED.
//
// Earlier we shipped an SW that cached /index.html forever, pinning every
// visitor to a stale JS bundle even after hard refresh. We've now stripped
// SW registration entirely and serve index.html/sw.js with no-cache headers.
// The paired /sw.js on disk is a self-destruct script that unregisters any
// previously-installed SW — so existing devices still need to briefly hit
// that script once to clean up, but we must NOT re-register it here,
// otherwise activate→unregister→reload→register cycles into an infinite
// reload loop.
//
// The cleanup runs only if an old SW is still registered on this device.
// Each controlled client gets unregistered ONCE; repeat visits are noops.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const reg of regs) { try { reg.unregister(); } catch {} }
  }).catch(() => {});
}
